import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { addPostToFeedCache, updatePostInFeedCache } from 'helpers/post/updatePostInFeedCache'
import {
  DEFAULT_REFRESH_BUFFER_MINUTES,
  DEFAULT_SIGNED_URL_TTL_MINUTES,
  computeExpiresAt,
  decorateMediaUrls,
  deepMergeSignedUrls,
  findMinSignedExpiryMs,
  shouldRefreshSignedUrl,
} from 'helpers/signedUrl'

interface FeedEntry {
  feed: any[]
  updatedAt: number
  expiresAt: number
  nextPageParam?: any | null
  hasNextPage?: boolean
}

interface SetFeedOpts {
  ttlMinutes?: number
  refreshBufferMinutes?: number
  nextPageParam?: any | null
  hasNextPage?: boolean
}

interface ProfileFeedCacheState {
  feedCache: Record<string, FeedEntry>
  setFeed: (cacheKey: string, freshFeed: any[], opts?: SetFeedOpts) => void
  getFeed: (cacheKey: string, opts?: { refreshBufferMinutes?: number }) => any[] | undefined
  getFeedEntry: (cacheKey: string) => FeedEntry | undefined
  getNextPageParam: (cacheKey: string) => any | null | undefined
  getHasNextPage: (cacheKey: string) => boolean | undefined
  isFresh: (cacheKey: string, opts?: { refreshBufferMinutes?: number }) => boolean
  removeFeed: (cacheKey: string) => void
  addPost: (cacheKey: string, post: any) => void
  updatePost: (post: any) => void
  updateVideoThumbnail: (
    postId: string,
    videoId: string,
    activeThumb: string
  ) => void

  updateVideoThumbnailState: (
    postId: string,
    videoId: string,
    changes: {
      active_thumb?: string | null
      thumbnail_generating?: boolean
    }
  ) => void

  updateUserAvatar: (
    userId: string,
    avatarUrl: string
  ) => void
    clear: () => void
  }

export const useProfileFeedCacheStore = create<ProfileFeedCacheState>()(
  persist(
    (set, get) => ({
      feedCache: {},

      setFeed: (cacheKey, freshFeed, opts) => {
        const ttlMinutes = opts?.ttlMinutes ?? DEFAULT_SIGNED_URL_TTL_MINUTES
        const refreshBufferMinutes = opts?.refreshBufferMinutes ?? DEFAULT_REFRESH_BUFFER_MINUTES

        const existing = get().feedCache[cacheKey]

/*         console.log(
          `[STORE setFeed] ${new Date().toISOString()} cacheKey=${cacheKey} freshLen=${(freshFeed as any)?.length ?? 'n/a'} ` +
            `hasExisting=${!!existing} existingExp=${existing?.expiresAt ?? 'none'} existingExpInSec=${
              existing?.expiresAt ? Math.round((existing.expiresAt - Date.now()) / 1000) : 'n/a'
            }`
        ) */

        const decoratedFresh = decorateMediaUrls(freshFeed)

        const merged =
          existing?.feed && Array.isArray(existing.feed)
            ? deepMergeSignedUrls(existing.feed, decoratedFresh, existing.expiresAt, refreshBufferMinutes)
            : decoratedFresh

        const realExpiryFromPayload = findMinSignedExpiryMs(merged)
        const syntheticExpiry = computeExpiresAt(ttlMinutes)
        const nextExpiresAt =
          realExpiryFromPayload == null
            ? syntheticExpiry
            : Math.min(
                realExpiryFromPayload,
                syntheticExpiry
              )

/*         const oldUrl = (existing?.feed as any)?.[0]?.photos?.[0]?.url
        const newUrl = (merged as any)?.[0]?.photos?.[0]?.url
        const freshUrl = (decoratedFresh as any)?.[0]?.photos?.[0]?.url */

/*         console.log(
          `[STORE setFeed MERGE] ${new Date().toISOString()} cacheKey=${cacheKey} usedMerge=${!!(
            existing?.feed && Array.isArray(existing.feed)
          )} existingExpInMin=${
            existing?.expiresAt ? Math.round((existing.expiresAt - Date.now()) / 60000) : 'n/a'
          } bufferMin=${refreshBufferMinutes}`
        )

        console.log(
          `[STORE setFeed URL0] old==merged=${oldUrl === newUrl} merged==fresh=${newUrl === freshUrl} ` +
            `old=${oldUrl?.slice(0, 90) ?? 'none'} ` +
            `fresh=${freshUrl?.slice(0, 90) ?? 'none'} ` +
            `merged=${newUrl?.slice(0, 90) ?? 'none'}`
        )

        console.log(
          `[STORE setFeed DONE] ${new Date().toISOString()} cacheKey=${cacheKey} nextExp=${nextExpiresAt} nextExpInMin=${Math.round(
            (nextExpiresAt - Date.now()) / 60000
          )} ttlMin=${ttlMinutes} realExpiry=${realExpiryFromPayload ?? 'null'}`
        )
 */
        const nextPageParam =
          Object.prototype.hasOwnProperty.call(opts ?? {}, 'nextPageParam')
            ? opts?.nextPageParam
            : existing?.nextPageParam

        const hasNextPage =
          Object.prototype.hasOwnProperty.call(opts ?? {}, 'hasNextPage')
            ? opts?.hasNextPage
            : existing?.hasNextPage

        set((state) => ({
          feedCache: {
            ...state.feedCache,
            [cacheKey]: {
              feed: merged,
              updatedAt: Date.now(),
              expiresAt: nextExpiresAt,
              nextPageParam,
              hasNextPage,
            },
          },
        }))
      },
addPost: (cacheKey, post) => {
  if (!post?.id) {
    console.log('[PROFILE FEED addPost] NO POST ID', {
      cacheKey,
      post
    })
    return
  }

  set(state => {
    const existing = state.feedCache[cacheKey]

    console.log('[PROFILE FEED addPost BEFORE]', {
      cacheKey,
      postId: post.id,
      existingLength: existing?.feed?.length ?? 0,
      existingIds: existing?.feed?.map(item => item?.id) ?? [],
      existingExpiresAt: existing?.expiresAt,
      now: Date.now()
    })

    if (
      existing?.feed?.some(
        item => String(item?.id) === String(post.id)
      )
    ) {
      console.log('[PROFILE FEED addPost] ALREADY EXISTS', {
        cacheKey,
        postId: post.id
      })

      return state
    }

    const nextEntry = {
      feed: [post, ...(existing?.feed ?? [])],
      updatedAt: Date.now(),
      expiresAt: computeExpiresAt(DEFAULT_SIGNED_URL_TTL_MINUTES),
      nextPageParam: existing?.nextPageParam,
      hasNextPage: existing?.hasNextPage
    }

    console.log('[PROFILE FEED addPost AFTER]', {
      cacheKey,
      postId: post.id,
      nextLength: nextEntry.feed.length,
      nextIds: nextEntry.feed.map(item => item?.id),
      expiresAt: nextEntry.expiresAt
    })

    return {
      feedCache: {
        ...state.feedCache,
        [cacheKey]: nextEntry
      }
    }
  })
},
      updatePost: post => {
        if (!post?.id) {
          return
        }

        set(state => {
          const feedCache =
            updatePostInFeedCache(
              state.feedCache,
              post
            )

          if (
            feedCache ===
            state.feedCache
          ) {
            return state
          }

          return {
            feedCache
          }
        })
      },
      getFeed: (cacheKey, opts) => {
        const refreshBufferMinutes = opts?.refreshBufferMinutes ?? DEFAULT_REFRESH_BUFFER_MINUTES
        const entry = get().feedCache[cacheKey]
        if (!entry) return undefined
        if (shouldRefreshSignedUrl(entry.expiresAt, refreshBufferMinutes)) return undefined
        return entry.feed
      },

      getFeedEntry: (cacheKey) => get().feedCache[cacheKey],

      getNextPageParam: (cacheKey) => get().feedCache[cacheKey]?.nextPageParam,

      getHasNextPage: (cacheKey) => get().feedCache[cacheKey]?.hasNextPage,

      isFresh: (cacheKey, opts) => {
        const refreshBufferMinutes = opts?.refreshBufferMinutes ?? DEFAULT_REFRESH_BUFFER_MINUTES
        const entry = get().feedCache[cacheKey]
        if (!entry) return false
        return !shouldRefreshSignedUrl(entry.expiresAt, refreshBufferMinutes)
      },

      removeFeed: (cacheKey) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [cacheKey]: _removed, ...newCache } = get().feedCache
        set({ feedCache: newCache })
      },

updateVideoThumbnailState: (
  postId,
  videoId,
  changes
) => {
  set(state => {
    let changed = false

    const feedCache = Object.fromEntries(
      Object.entries(state.feedCache).map(
        ([cacheKey, entry]) => {
          const postIndex = entry.feed.findIndex(
            post =>
              post?.id === postId
          )

          if (postIndex === -1) {
            return [cacheKey, entry]
          }

          const post = entry.feed[postIndex]

          if (!Array.isArray(post?.videos)) {
            return [cacheKey, entry]
          }

          const videoIndex = post.videos.findIndex(
            (video: any) =>
              video?.id === videoId
          )

          if (videoIndex === -1) {
            return [cacheKey, entry]
          }

          const currentVideo =
            post.videos[videoIndex]

          const nextVideo = {
            ...currentVideo,
            ...changes,
          }

          const isSame =
            currentVideo.active_thumb ===
              nextVideo.active_thumb &&
            Boolean(
              currentVideo.thumbnail_generating
            ) ===
              Boolean(
                nextVideo.thumbnail_generating
              )

          if (isSame) {
            return [cacheKey, entry]
          }

          changed = true

          const videos = [...post.videos]

          videos[videoIndex] = nextVideo

          const feed = [...entry.feed]

          feed[postIndex] = {
            ...post,
            videos,
          }

          return [
            cacheKey,
            {
              ...entry,
              feed,
            },
          ]
        }
      )
    )

    return changed
      ? {
          feedCache,
        }
      : state
  })
},

updateVideoThumbnail: (
  postId: string,
  videoId: string,
  activeThumb: string
) => {
  if (!activeThumb) return

  set(state => {
    let changed = false

    const nextFeedCache = Object.fromEntries(
      Object.entries(state.feedCache).map(([cacheKey, entry]) => {
        const postIndex = entry.feed.findIndex(
          post => post?.id === postId
        )

        if (postIndex === -1) {
          return [cacheKey, entry]
        }

        const existingPost = entry.feed[postIndex]

        if (!Array.isArray(existingPost?.videos)) {
          return [cacheKey, entry]
        }

        const videoIndex = existingPost.videos.findIndex(
          (video: any) => video?.id === videoId
        )

        if (videoIndex === -1) {
          return [cacheKey, entry]
        }

        const existingVideo = existingPost.videos[videoIndex]

        if (existingVideo?.active_thumb === activeThumb) {
          return [cacheKey, entry]
        }

        changed = true

        const nextVideos = [...existingPost.videos]

        nextVideos[videoIndex] = {
          ...existingVideo,
          active_thumb: activeThumb,
        }

        const nextFeed = [...entry.feed]

        nextFeed[postIndex] = {
          ...existingPost,
          videos: nextVideos,
        }

        return [
          cacheKey,
          {
            ...entry,
            feed: nextFeed,
            updatedAt: Date.now(),
          },
        ]
      })
    )

    if (!changed) {
      return state
    }

    return {
      feedCache: nextFeedCache,
    }
  })
},
updateUserAvatar: (
  userId: string,
  avatarUrl: string
) => {
  if (!avatarUrl) return

  set(state => {
    let changed = false

    const nextFeedCache = Object.fromEntries(
      Object.entries(state.feedCache).map(
        ([cacheKey, entry]) => {
          let feedChanged = false

          const nextFeed = entry.feed.map(post => {
            if (post?.user?.id !== userId) {
              return post
            }

            const currentAvatarUrl =
              post?.user?.avatar?.url

            if (currentAvatarUrl === avatarUrl) {
              return post
            }

            changed = true
            feedChanged = true

            return {
              ...post,
              user: {
                ...post.user,
                avatar: {
                  ...(post.user?.avatar ?? {}),
                  url: avatarUrl,
                },
              },
            }
          })

          if (!feedChanged) {
            return [cacheKey, entry]
          }

          return [
            cacheKey,
            {
              ...entry,
              feed: nextFeed,
              updatedAt: Date.now(),
            },
          ]
        }
      )
    )

    if (!changed) {
      return state
    }

    return {
      feedCache: nextFeedCache,
    }
  })
},
      clear: () => set({ feedCache: {} }),
    }),
    {
      name: 'profile-feed-cache',
      partialize: (state) => ({ feedCache: state.feedCache }),
    }
  )
)